import React, { useState } from "react";
import Modal from "components/UI/DialogModal";
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import MuiButton from "components/UI/MuiButton";
import { useTranslation } from "react-i18next";
import { IDepartments } from "models/departments";
import { useAlert } from "contexts/alertContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { ICompanyDepartments } from "models/companyDepartment";
import { ICompanyEmployees } from "models/company";

interface Props {
  open: boolean;
  loading: boolean;
  CompanyDepartments: ICompanyDepartments[];
  companyEmployees: ICompanyEmployees[]
  onClose: () => void;
  onSubmit: (data: any[]) => void;
}

type Entry = {
  Name: string;
  NameAr: string;
  HeadOfDepartment: string;
  ApprovingOfficer: string;
};


const isTiTleExist = (data: ICompanyDepartments[], title: string, titleAr: string) => {
  for (const item of data) {
    if (
      item?.name?.trim().toLocaleLowerCase() === title.trim().toLocaleLowerCase() ||
      item?.nameAr?.trim() === titleAr.trim()
    ) {
      return true;
    }
  }
}


const AddCompanyDepartment: React.FC<Props> = ({
  open,
  loading,
  onClose,
  onSubmit,
  CompanyDepartments,
  companyEmployees
}) => {
  const [title, setTitle] = useState<string>("");
  const [titleAr, setTitleAr] = useState<string>("");
  const [HOD, setHOD] = useState<string>("");
  const [approving, setApproving] = useState<string>("");
  const [entries, setEntries] = useState<Entry[]>([]);
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  const handleAddEntry = () => {



    if (!isTiTleExist(CompanyDepartments, title, titleAr)) {
      const newEntry = {
        Name: title,
        NameAr: titleAr,
        HeadOfDepartment: HOD,
        ApprovingOfficer: approving
      };

      setEntries([...entries, newEntry]);
      setTitle("");
      setTitleAr("");
      setHOD('')
      setApproving('')
    } else {
      setAlert({
        open: true,
        message: "Title or TitleAr Already Exists",
        type: "info",
      });
    }



  };

  const handleDeleteEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t("T_add_department")}
        maxWidth="xl"

        children={
          <Stack spacing={2} >
            <div>
              <div style={{ width: "100%", marginTop: "20px" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={t("T_title")}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label={t("T_title_Ar")}
                      value={titleAr}
                      onChange={(e) => setTitleAr(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>{t("T_hod")}</InputLabel>
                      <Select
                        value={HOD}
                        onChange={(e) => setHOD(e.target.value as string)}
                        label={t("T_hod")}
                      >
                        {companyEmployees.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>{t("T_approving_officer")}</InputLabel>
                      <Select
                        value={approving}
                        onChange={(e) => setApproving(e.target.value as string)}
                        label={t("T_approving_officer")}
                      >
                        {companyEmployees.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      style={{ borderRadius: 0, height: '100%' }}
                      onClick={handleAddEntry}
                      disabled={!title || !titleAr}
                    >
                      {t("T_add")}
                    </Button>
                  </Grid>
                </Grid>
              </div>


              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("T_title")}</TableCell>
                    <TableCell>{t("T_title_Ar")}</TableCell>
                    <TableCell>{t("T_title")}</TableCell>
                    <TableCell>{t("T_title_Ar")}</TableCell>
                    <TableCell>{t("T_action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>{entry.Name}</TableCell>
                      <TableCell>{entry.NameAr}</TableCell>
                      <TableCell>{companyEmployees.find(item => item.id === entry.HeadOfDepartment)?.name}</TableCell>
                      <TableCell>{companyEmployees.find(item => item.id === entry.ApprovingOfficer)?.name}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleDeleteEntry(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Stack>
        }
        actions={
          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
            px={1}
          >
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "warning.main", color: "white" }}
              size="large"
              onClick={onClose}
            >
              {t("T_cancel")}
            </MuiButton>
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "primary.main", color: "white" }}
              disabled={loading}
              size="large"
              onClick={() => onSubmit(entries)}
            >
              {t("T_add")}
              <CircularProgress
                size={10}
                sx={{ mx: 2, display: loading ? "" : "none" }}
              />
            </MuiButton>
          </Grid>
        }
      />
    </>
  );
};

export default AddCompanyDepartment;
